// ./src/utils/api/MarketingChannelService.ts
//
// Marketing channel service. A marketing channel is an ACCOUNT-LEVEL, reusable
// `?ch=` traffic-source marker (fb, insta, poster1) that is applied across every
// event: it's defined once per account and per-event pages only generate
// event-specific tracking URLs / QR codes from it. That's what makes cross-event
// conversion comparison possible. OpResult-native. Account-owned: every read is
// scoped to the caller's account and only the caller's own channels are editable
// (see the `rights` field). A multi-id update/remove is a bulk op (server
// restricts a bulk update to status). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

export const MARKETING_CHANNEL_FIELDS = `
  id
  accountId
  name
  code
  description
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const MARKETING_CHANNEL_LIST_QUERY = gql`
  query MarketingChannelList($filter: MarketingChannelFilter, $params: PaginationAndSorting) {
    marketingChannelList(filter: $filter, params: $params) {
      code
      total
      data { ${MARKETING_CHANNEL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKETING_CHANNEL_GET_QUERY = gql`
  query MarketingChannelGet($id: ID) {
    marketingChannelGet(id: $id) {
      code
      data { ${MARKETING_CHANNEL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKETING_CHANNEL_CREATE_MUTATION = gql`
  mutation MarketingChannelCreate($params: MarketingChannelInput) {
    marketingChannelCreate(params: $params) {
      code
      data { ${MARKETING_CHANNEL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKETING_CHANNEL_UPDATE_MUTATION = gql`
  mutation MarketingChannelUpdate($where: MarketingChannelWhereInput, $params: MarketingChannelInput) {
    marketingChannelUpdate(where: $where, params: $params) {
      code
      data { ${MARKETING_CHANNEL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKETING_CHANNEL_REMOVE_MUTATION = gql`
  mutation MarketingChannelRemove($where: MarketingChannelWhereInput) {
    marketingChannelRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api MarketingChannel schema)
// =============================================================================

export interface MarketingChannel {
  id?: string;
  accountId?: string | null;
  /** Human channel name (e.g. "Facebook", "Paper magazine"). */
  name?: string | null;
  /** The ?ch= marker appended to event website URLs (url-safe, e.g. fb, insta, poster1). */
  code?: string | null;
  description?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface MarketingChannelInput {
  name?: string | null;
  /** The ?ch= marker; normalized to a url-safe token server-side. */
  code?: string | null;
  description?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** Auth context — the event page the write is issued from (channels are account-level; lets crew
   *  with event.operate on this event manage the event's account's channels). Not stored. */
  eventId?: string | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface MarketingChannelBulkInput {
  status?: number | null;
  /** Auth context (see MarketingChannelInput.eventId). */
  eventId?: string | null;
}

export interface MarketingChannelFilter {
  id?: string[] | null;
  code?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /** Auth/scoping context — the event page the list is shown on (scopes crew reads to that event's account). */
  eventId?: string | null;
}

export interface MarketingChannelWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
  /** Auth context (see MarketingChannelInput.eventId) — used by remove. */
  eventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class MarketingChannelService {
  static async list(
    filter?: MarketingChannelFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(MARKETING_CHANNEL_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(MARKETING_CHANNEL_GET_QUERY, { id });
  }

  static async create(params: MarketingChannelInput): Promise<OpResult> {
    return ApiService.executeMutation(MARKETING_CHANNEL_CREATE_MUTATION, { params });
  }

  static async update(where: MarketingChannelWhereInput, params: MarketingChannelInput): Promise<OpResult> {
    return ApiService.executeMutation(MARKETING_CHANNEL_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, eventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(MARKETING_CHANNEL_REMOVE_MUTATION, {
      where: { id, ...(eventId ? { eventId } : {}) },
    });
  }

  /** Mass-change status on every selected channel in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: MarketingChannelBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MARKETING_CHANNEL_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected channel in one call. */
  static async removeMany(ids: string[], eventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MARKETING_CHANNEL_REMOVE_MUTATION, {
      where: { id: ids, ...(eventId ? { eventId } : {}) },
    });
  }
}
